<template>
	<div>
		<!-- Submenu -->
		<product-detail-submenu />

		<core-layout-content medium>
			<!-- Language selector -->
			<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
				<div class="pr2 xs-12 fs3 lh125 fw7 pb1">{{ $t("language") }}</div>
				<core-select-locale
					class="xs-12"
					size="xsmall"
					v-model="selectedLocale"
				/>
			</div>
			<template v-if="!isLoading">
				<!-- Product Item -->
				<products-list-item
					:name="product.name"
					:plan="product.plan"
					:version="product.version"
					:type="product.type"
					:insurerName="product.insurerName"
					:insurerLogoUrl="product.insurerLogoUrl"
					:selectedLocale="selectedLocale"
					@updated="getProduct"
				>
					<template slot="action">
						<!-- <j-button>{{ $t("edit") }}</j-button> -->
					</template>
				</products-list-item>

				<!-- If product doesn't exist -->
				<j-card
					v-if="!isLoading && product.length === 0"
					class="bg5 ta2 p2 cError fw7"
				>
					{{ $t("errors.product_doesnt_exist") }}
				</j-card>

				<!-- Tags -->
				<div class="flex xs-12 xs-between mt4">
					<div class="fs5 lh125 fw7 c1 mb2">{{ $t("tags") }}</div>
					<j-button @click="$modal.show('addTag')" type="tertiary">
						<crm-icon name="add" size="small" color="c2" />{{ $t("add") }}
					</j-button>
				</div>
				<j-card v-if="tags.length === 0" class="mb2 bg4--l3 p4 ta2 fs3 c6 ">
					{{ $t("errors.no_data") }}
				</j-card>
				<j-card v-else class="flex p2 bg5">
					<div v-for="(item, i) in tags" :key="i" class="p2 r2 bg2 c5 mr2 flex">
						<div>{{ item.type }}</div>
						<span
							class="cu2 c7 ml2"
							v-if="item.type !== 'product'"
							@click="removeTagFromProduct(item)"
							>&times;</span
						>
					</div>
				</j-card>

				<!-- Add Tagg -->
				<core-modal name="addTag" :headline="$t('add_tag')">
					<div class="bg5 p2">
						<j-base-input-text v-model="tag" size="small" class="mb2" />
						<div class="flex xs-right">
							<j-button size="small" @click="addTagToProduct">{{
								$t("add")
							}}</j-button>
						</div>
					</div>
				</core-modal>
			</template>
		</core-layout-content>
	</div>
</template>

<script>
import ProductDetailSubmenu from "../ProductDetailSubmenu"
import ProductsListItem from "../ProductsListItem"
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputText from "@/components/JBaseInputText"

import { i18n } from "@/locales/setup.js"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		ProductDetailSubmenu,
		ProductsListItem,
		JCard,
		JButton,
		CoreLayoutContent,
		CoreSelectLocale,
		CrmIcon,
		CoreModal,
		JBaseInputText
	},

	props: {
		plan: { type: String, required: true },
		type: { type: String, required: true },
		version: {
			validator: val => typeof val === "string" || val === null,
			required: true,
			default: null
		}
	},

	data: () => ({
		isLoading: false,
		selectedLocale: i18n.locale,
		list: [],
		tag: null
	}),

	computed: {
		/**
		 * What we display
		 */
		product() {
			const list = this.list.map(item => ({
				name: item.name,
				plan: item?.productId?.plan,
				type: item?.productId?.type,
				version: item?.productId?.version,
				insurerName: item?.insurer?.name,
				insurerLogoUrl: item?.insurer?.logoUrls?.typeC
			}))

			return list.length > 0 ? list[0] : {}
		},
		tags() {
			return this.list?.[0]?.tags || []
		}
	},

	methods: {
		async getProduct() {
			const { plan, version, type } = this

			this.isLoading = true

			const query = gql`
				query products(
					$skip: Int
					$limit: Int
					$sort: sortInput
					$where: productWhereInput
				) {
					products_2(skip: $skip, limit: $limit, sort: $sort, where: $where) {
						totalCount
						list {
							createdAt
							createdBy {
								associatedUser {
									id
									name
								}
							}
							lastModifiedAt
							lastModifiedBy {
								associatedUser {
									id
									name
								}
							}
							name
							productId {
								plan
								type
								version
							}
							insurer {
								id
								name
								logoUrls {
									typeC
								}
							}
							tags {
								id
								type
							}
						}
					}
				}
			`
			const variables = {
				where: { productId: { plan, version, type } }
			}
			const res = await api({ query, variables, locale: this.selectedLocale })
			this.isLoading = false
			this.list = res?.data?.products_2?.list
		},

		async addTagToProduct() {
			const { plan, version, type } = this
			this.isLoading = true

			const query = gql`
				mutation addTagToProduct(
					$productId: productIdInput!
					$input: tagInput!
				) {
					addTagToProduct(productId: $productId, input: $input) {
						status
						errors
					}
				}
			`
			const variables = {
				productId: { plan, version, type },
				input: {
					type: this.tag
				}
			}
			const res = await api({ query, variables, locale: this.selectedLocale })
			this.isLoading = false

			if (res?.data?.addTagToProduct?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.addTagToProduct?.errors?.[0] ||
							this.$t("errors.general")
					}
				})
			}

			this.tag = null
			this.getProduct()
		},

		async removeTagFromProduct(payload) {
			const { plan, version, type } = this
			this.isLoading = true

			const query = gql`
				mutation removeTagFromProduct(
					$productId: productIdInput!
					$id: String!
				) {
					removeTagFromProduct(productId: $productId, id: $id) {
						status
						errors
					}
				}
			`
			const variables = {
				productId: { plan, version, type },
				id: payload?.id
			}
			const res = await api({ query, variables, locale: this.selectedLocale })
			this.isLoading = false

			if (res?.data?.removeTagFromProduct?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.removeTagFromProduct?.errors?.[0] ||
							this.$t("errors.general")
					}
				})
			}

			this.getProduct()
		}
	},

	created() {
		// Check if we are receiving all props
		// If not, redirect to products listing
		if (
			typeof this.plan === "undefined" ||
			typeof this.type === "undefined" ||
			typeof this.version === "undefined"
		) {
			this.$router.push({ name: "ProductsList" })
			return
		}

		// Fetch product info
		this.getProduct()
	},

	watch: {
		selectedLocale: {
			handler(o, n) {
				if (o && n) {
					this.getProduct()
				}
			}
		}
	}
}
</script>
